<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMessageContext } from '../provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { downloadFile } from '@chatwoot/utils';
import BaseAttachmentBubble from './BaseAttachment.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Button from 'next/button/Button.vue';
import MediaExtractedContent from './MediaExtractedContent.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { attachments } = useMessageContext();

const { t } = useI18n();
const showPreview = ref(false);
const isDownloading = ref(false);

const url = computed(() => {
  return attachments.value[0].dataUrl;
});

const fileName = computed(() => {
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return fileName.value.split('.').pop();
});

const isPdf = computed(
  () =>
    attachments.value[0]?.extension?.toLowerCase() === 'pdf' ||
    fileType.value?.toLowerCase() === 'pdf'
);

const previewAttachment = computed(() =>
  useSnakeCase(attachments.value[0], { deep: true })
);

const downloadAttachment = async () => {
  const attachment = attachments.value[0];

  try {
    isDownloading.value = true;
    await downloadFile({
      url: attachment.dataUrl,
      type: attachment.fileType,
      extension: attachment.extension,
    });
  } finally {
    isDownloading.value = false;
  }
};

const action = computed(() => {
  if (isPdf.value) {
    return {
      label: t('GALLERY_VIEW.PREVIEW_PDF'),
      icon: 'i-lucide-file-search',
      onClick: () => {
        showPreview.value = true;
      },
    };
  }

  return {
    href: url.value,
    label: t('CONVERSATION.DOWNLOAD'),
    icon: 'i-lucide-download',
  };
});
</script>

<template>
  <div class="flex flex-col">
    <BaseAttachmentBubble
      icon="i-teenyicons-user-circle-solid"
      icon-bg-color="bg-n-alpha-3 dark:bg-n-alpha-white"
      sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.FILE"
      :content="decodeURI(fileName)"
      :action="action"
    >
      <template #icon>
        <FileIcon :file-type="fileType" class="size-4" />
      </template>
      <template v-if="isPdf" #actions>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button
            variant="faded"
            color="slate"
            size="sm"
            class="w-full"
            :label="t('GALLERY_VIEW.PREVIEW_PDF')"
            icon="i-lucide-file-search"
            @click="showPreview = true"
          />
          <Button
            variant="faded"
            color="slate"
            size="sm"
            class="w-full"
            :label="t('CONVERSATION.DOWNLOAD')"
            icon="i-lucide-download"
            :is-loading="isDownloading"
            :disabled="isDownloading"
            @click="downloadAttachment"
          />
        </div>
      </template>
      <template #after>
        <MediaExtractedContent :attachment-id="attachments[0].id" />
      </template>
    </BaseAttachmentBubble>
    <GalleryView
      v-if="showPreview"
      v-model:show="showPreview"
      :attachment="previewAttachment"
      :all-attachments="[previewAttachment]"
      @close="showPreview = false"
    />
  </div>
</template>
